import type { PropertyId } from "~/classes/entity"
import type { Gesture } from "~/classes/gestures/BaseGesture"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import useAgentFileDropToAdd from "~/classes/gestures/useAgentFileDropToAdd"
import useClipboardKeyboardShortcuts from "~/classes/gestures/useClipboardKeyboardShortcuts"
import useCollectionViewBackgroundClickToBlur from "~/classes/gestures/useCollectionViewBackgroundClickToBlur"
import useCollectionViewBackgroundDragToMultiSelect from "~/classes/gestures/useCollectionViewBackgroundDragToMultiSelect"
import useCollectionViewBackgroundDropToMove from "~/classes/gestures/useCollectionViewBackgroundDropToMove"
import useCollectionViewFileClickPropertyToEdit from "~/classes/gestures/useCollectionViewFileClickPropertyToEdit"
import useCollectionViewFileClickToSelectOrOpen from "~/classes/gestures/useCollectionViewFileClickToSelectOrOpen"
import useCollectionViewFileDragToStartMove from "~/classes/gestures/useCollectionViewFileDragToStartMove"
import useCollectionViewFileDropOnFolderToMove from "~/classes/gestures/useCollectionViewFileDropOnFolderToMove"
import useColumnViewClickToExpandFolder from "~/classes/gestures/useColumnViewClickToExpandFolder"
import useColumnViewDragToResize from "~/classes/gestures/useColumnViewDragToResize"
import useContextMenuClick from "~/classes/gestures/useContextMenuClick"
import useDesktopKeyboardShortcut from "~/classes/gestures/useDesktopKeyboardShortcut"
import useDragExternalFilesInToUpload from "~/classes/gestures/useDragExternalFilesInToUpload"
import useDragExternalLinksInToUpload from "~/classes/gestures/useDragExternalLinksInToUpload"
import useGlobalKeyboardShortcut from "~/classes/gestures/useGlobalKeyboardShortcut"
import useInstantSearchKeyboardShortcuts from "~/classes/gestures/useInstantSearchKeyboardShortcuts"
import useMouseGesturesOnPeek from "~/classes/gestures/useMouseGesturesOnPeek"
import usePanelDragToResize from "~/classes/gestures/usePanelDragToResize"
import usePathDropOnFolderToMove from "~/classes/gestures/usePathDropOnFolderToMove"
import usePathFileClickToOpen from "~/classes/gestures/usePathFileClickToOpen"
import usePathFileDragToMove from "~/classes/gestures/usePathFileDragToMove"
import usePeekViewBackgroundClickToClosePeek from "~/classes/gestures/usePeekViewBackgroundClickToClosePeek"
import usePreviewShortcuts from "~/classes/gestures/usePreviewShortcuts"
import useSelectionKeyboardShortcut from "~/classes/gestures/useSelectionKeyboardShortcut"
import useSidebarBackgroundClickToDeselect from "~/classes/gestures/useSidebarBackgroundClickToDeselect"
import useSidebarDragToResize from "~/classes/gestures/useSidebarDragToResize"
import useSidebarFileDragToMove from "~/classes/gestures/useSidebarFileDragToMove"
import useSidebarFileDropToAddToFavorites from "~/classes/gestures/useSidebarFileDropToAddToFavorites"
import useSidebarFileDropToMoveIntoRoots from "~/classes/gestures/useSidebarFileDropToMoveIntoRoots"
import useSidebarFileDropToMoveToLocation from "~/classes/gestures/useSidebarFileDropToMoveToLocation"
import useSidebarItemClickToSelectOrOpen from "~/classes/gestures/useSidebarItemClickToSelectOrOpen"
import useTableViewDragToResize from "~/classes/gestures/useTableViewDragToResize"
import useTreeViewClickToExpandFolder from "~/classes/gestures/useTreeViewClickToExpandFolder"
import useWaterfallFileClickToSelectOrOpen from "~/classes/gestures/useWaterfallFileClickToSelectOrOpen"
import { isMac } from "~/utils/platform"
import useAgentKeyboardShortcuts from "~/classes/gestures/useAgentKeyboardShortcuts"

interface GestureState {
  initialized: boolean
  gesture: GestureModeType
  mouseButtonsPressed: Set<number>
  keysPressed: Set<string>
  mainView: {
    scrollX: number
    scrollY: number
  }
  mousePosition?: {
    clientX: number
    clientY: number
  }
  mouseDown?: {
    clientX: number
    clientY: number
  }
  filesDragged?: StaticFile[]
  sidebarItemDraggedOver?: {
    /** A random, assigned-at-creation id that each element can use for identification */
    elementId: string
    file?: StaticFile
    search?: StaticFile
    conversation?: StaticFile
  }
  sidebarFavoritesDraggedBetween?: [string | undefined, string | undefined]
  collectionFileDraggedOver?: StaticFile
  pathFileDraggedOver?: StaticFile
  searchDragged?: StaticSearch
  desktop: {
    // eslint-disable-next-line ts/no-unsafe-function-type
    dragAndDropHandler?: Function
  }
}

class Gestures extends GlobalComposable<GestureState> {
  gestures: Gesture[] = []

  override defaultState = (): GestureState => ({
    initialized: false,
    gesture: "none",
    mouseButtonsPressed: new Set(),
    keysPressed: new Set(),
    mainView: { scrollX: 0, scrollY: 0 },
    desktop: {},
  })

  override clearState = () => {
    this.state.value = this.defaultState()
    this.gestures = []
  }

  isDragging = computed(() => {
    return [
      "drag-file",
      "drag-search",
      "drag-select",
      "drag-in-unknown-file",
      "drag-in-unknown-link",
      "drag-divider",
    ].includes(this.state.value.gesture)
  })

  /**
   * We use layers to order our gestures handlers, so that we
   * can easily mount a new gesture into the right place.
   * @deprecated use the exported one from `BaseGesture`
   */
  gestureLayers = {
    peek: 610,
    drag: 600,
    search: 500,
    contextMenu: 420,
    overlay: 400,
    navigation: 300,
    titleBar: 200,
    sidebar: 160,
    panel: 150,
    viewMode: 140,
    mainViewProperty: 130,
    mainView: 120,
    mainViewBackground: 110,
    base: 100,
    selectionKeyboardShortcut: 90,
    keyboardShortcut: 80,
  }

  initGestures = async () => {
    if (this.state.value.initialized)
      return this.warn("Gesture state is already initialized, skipping re-initialization")

    // this is likely a bunch of no-ops but it might help eradicate duplication issues?
    this.destroyGestures()

    this.state.value.initialized = true

    if (isDesktop()) {
      this.debug("registering drag and drop event")
      const { registerDragAndDropEvent } = useTauri()
      this.state.value.desktop.dragAndDropHandler = await registerDragAndDropEvent()
    }

    if (document) {
      document.body.addEventListener("mousedown", this.onGestureEvent, true)
      document.body.addEventListener("mouseup", this.onGestureEvent, true)
      document.body.addEventListener("mousemove", this.onGestureEvent, true)
      document.body.addEventListener("mouseenter", this.onGestureEvent, true)
      document.body.addEventListener("mouseleave", this.onGestureEvent, true)
      document.body.addEventListener("contextmenu", this.onGestureEvent, false)
      document.body.addEventListener("cut", this.onGestureEvent)
      document.body.addEventListener("copy", this.onGestureEvent)
      document.body.addEventListener("paste", this.onGestureEvent)
      document.addEventListener("keydown", this.onGestureEvent)
      document.addEventListener("keyup", this.onGestureEvent)
      document.addEventListener("wheel", this.onGestureEvent)
    }

    if (window) {
      window.addEventListener("dragover", this.onGestureEvent, false)
      window.addEventListener("dragenter", this.onGestureEvent, false)
      window.addEventListener("dragleave", this.onGestureEvent, false)
      window.addEventListener("drop", this.onGestureEvent, false)
      window.addEventListener("blur", this.resetKeyboardState)
      window.addEventListener("focus", this.resetKeyboardState)
    }

    // Initializes the gesture list, which is traversed from highest to
    // lowest priority to determine the handler for a particular action/event,
    // so ordering does matter here. We use the `capture()` method inside
    // each gesture to signal that this gesture handler has captured the event,
    // and no further handlers can see it.
    // NOTE: The binary tree of gestures preserves insertion order, so these
    // insertions go from lowest to highest priority

    // Sidebar Mouse Gestures
    this.gestures.push(useSidebarDragToResize())
    this.gestures.push(useSidebarFileDropToAddToFavorites())
    this.gestures.push(useSidebarFileDropToMoveToLocation())
    this.gestures.push(useSidebarFileDropToMoveIntoRoots())
    this.gestures.push(useSidebarFileDragToMove())
    this.gestures.push(useSidebarItemClickToSelectOrOpen())
    this.gestures.push(useSidebarBackgroundClickToDeselect())

    // File drop events (need to take precedence over clicks)
    this.gestures.push(useAgentFileDropToAdd())
    this.gestures.push(useDragExternalFilesInToUpload())
    this.gestures.push(useDragExternalLinksInToUpload())
    this.gestures.push(usePathDropOnFolderToMove())
    this.gestures.push(useCollectionViewFileDropOnFolderToMove())
    this.gestures.push(useCollectionViewBackgroundDropToMove())
    // TODO: drop into a new window event, or a new tab event

    // Peek Mouse Gestures
    this.gestures.push(useMouseGesturesOnPeek())

    // Panel Type Mouse Gestures
    this.gestures.push(useWaterfallFileClickToSelectOrOpen())

    // Panel Mouse Gestures
    this.gestures.push(usePanelDragToResize())

    // Instant Nav Gestures

    // Context Menu Gestures
    this.gestures.push(useContextMenuClick())

    // Nav Views Gestures
    this.gestures.push(usePathFileDragToMove())
    this.gestures.push(usePathFileClickToOpen())

    // View mode specific gestures
    this.gestures.push(useTreeViewClickToExpandFolder())
    this.gestures.push(useColumnViewClickToExpandFolder())
    this.gestures.push(useColumnViewDragToResize())
    this.gestures.push(useTableViewDragToResize())

    // Main View Gestures
    this.gestures.push(usePeekViewBackgroundClickToClosePeek())
    this.gestures.push(useCollectionViewFileDragToStartMove())
    this.gestures.push(useCollectionViewFileClickPropertyToEdit())
    this.gestures.push(useCollectionViewFileClickToSelectOrOpen())
    this.gestures.push(useCollectionViewBackgroundClickToBlur())
    this.gestures.push(useCollectionViewBackgroundDragToMultiSelect())

    // Clipboard events
    this.gestures.push(useAgentKeyboardShortcuts())
    this.gestures.push(useClipboardKeyboardShortcuts())

    // Instant Nav keyboard events
    this.gestures.push(useInstantSearchKeyboardShortcuts())

    // Selection keyboard events
    this.gestures.push(useSelectionKeyboardShortcut())

    // Contextual keyboard events
    this.gestures.push(usePreviewShortcuts())
    this.gestures.push(useDesktopKeyboardShortcut())
    this.gestures.push(useGlobalKeyboardShortcut())

    this.gestures.sort((a, b) => b.layer - a.layer)

    for (const gesture of this.gestures)
      gesture.onMounted()
  }

  destroyGestures = () => {
    document.body.removeEventListener("mousedown", this.onGestureEvent)
    document.body.removeEventListener("mouseup", this.onGestureEvent)
    document.body.removeEventListener("mousemove", this.onGestureEvent)
    document.body.removeEventListener("mouseenter", this.onGestureEvent)
    document.body.removeEventListener("mouseleave", this.onGestureEvent)
    document.body.removeEventListener("contextmenu", this.onGestureEvent)
    document.body.removeEventListener("cut", this.onGestureEvent)
    document.body.removeEventListener("copy", this.onGestureEvent)
    document.body.removeEventListener("paste", this.onGestureEvent)
    document.removeEventListener("keydown", this.onGestureEvent)
    document.removeEventListener("keyup", this.onGestureEvent)
    document.removeEventListener("wheel", this.onGestureEvent)

    if (window) {
      window.removeEventListener("dragover", this.onGestureEvent)
      window.removeEventListener("dragenter", this.onGestureEvent)
      window.removeEventListener("dragleave", this.onGestureEvent)
      window.removeEventListener("drop", this.onGestureEvent)
      window.removeEventListener("blur", this.resetKeyboardState)
      window.removeEventListener("focus", this.resetKeyboardState)
    }

    if (isDesktop() && this.state.value.desktop.dragAndDropHandler)
      this.state.value.desktop.dragAndDropHandler()

    for (const gesture of this.gestures)
      gesture.onUnmounted()

    this.state.value.initialized = false
  }

  mountGesture = (gesture: Gesture) => {
    for (const existing of this.gestures)
      if (existing === gesture)
        throw new Error("Cannot add duplicate gesture")
    this.gestures.push(gesture)
    this.gestures.sort((a, b) => b.layer - a.layer)

    // If the gestures have already been initialized, we need to mount it here,
    // otherwise it will get mounted by the gesture system anyway
    if (this.state.value.initialized)
      gesture.onMounted()
  }

  unmountGesture = (gesture: Gesture) => {
    if (!this.state.value.initialized)
      throw new Error("Gestures not initialized yet")
    const ind = this.gestures.findIndex(g => g === gesture)
    if (ind === -1)
      return
    this.gestures.splice(ind, 1)
    gesture.onUnmounted()
  }

  // TODO: do we want to preventDefault when we capture an action?
  onGestureEvent = async (event: MouseEvent | KeyboardEvent | DragEvent | ClipboardEvent | WheelEvent) => {
    let callback: (g: Gesture) => Promise<void>

    if (event.type === "mousedown") {
      const mouse = event as MouseEvent
      this.updateMouseButtonsPressedFromEvent(mouse)
      this.state.value.mouseDown = {
        clientX: mouse.clientX,
        clientY: mouse.clientY,
      }
      callback = g => g.onMouseDown(mouse)
    }
    else if (event.type === "mouseup") {
      const mouse = event as MouseEvent
      this.updateMouseButtonsPressedFromEvent(mouse)
      callback = g => g.onMouseUp(mouse)
    }
    else if (event.type === "mousemove") {
      const mouse = event as MouseEvent
      this.updateMousePositionFromEvent(mouse)
      callback = g => g.onMouseMove(mouse)
    }
    else if (event.type === "mouseenter") {
      const mouse = event as MouseEvent
      this.updateMouseButtonsPressedFromEvent(mouse)
      this.updateMousePositionFromEvent(mouse)
      callback = g => g.onMouseEnter(mouse)
    }
    else if (event.type === "mouseleave") {
      const mouse = event as MouseEvent
      callback = g => g.onMouseLeave(mouse)
    }
    else if (event.type === "wheel") {
      const wheel = event as WheelEvent
      callback = g => g.onWheel(wheel)
    }
    else if (event.type === "contextmenu") {
      const mouse = event as MouseEvent
      this.updateMousePositionFromEvent(mouse)
      callback = g => g.onContextMenu(mouse)
    }
    else if (event.type === "keydown") {
      const keyboard = event as KeyboardEvent
      this.state.value.keysPressed.add(keyboard.key)
      callback = g => g.onKeyDown(keyboard)
    }
    else if (event.type === "keyup") {
      const keyboard = event as KeyboardEvent
      this.state.value.keysPressed.delete(keyboard.key)
      callback = g => g.onKeyUp(keyboard)
    }
    else if (event.type === "dragenter") {
      event.preventDefault()
      const drag = event as DragEvent
      callback = g => g.onDragEnter(drag)
    }
    else if (event.type === "dragleave") {
      event.preventDefault()
      const drag = event as DragEvent
      callback = g => g.onDragLeave(drag)
    }
    else if (event.type === "dragover") {
      event.preventDefault()
      const drag = event as DragEvent
      this.state.value.mousePosition = {
        clientX: drag.clientX,
        clientY: drag.clientY,
      }
      callback = g => g.onDragOver(drag)
    }
    else if (event.type === "drop") {
      event.preventDefault()
      const drag = event as DragEvent
      callback = g => g.onDrop(drag)
    }
    else if (event.type === "cut") {
      const clipboard = event as ClipboardEvent
      callback = g => g.onCut(clipboard)
    }
    else if (event.type === "copy") {
      const clipboard = event as ClipboardEvent
      callback = g => g.onCopy(clipboard)
    }
    else if (event.type === "paste") {
      const clipboard = event as ClipboardEvent
      callback = g => g.onPaste(clipboard)
    }
    else {
      throw new Error(`Unknown event type: ${event.type}`)
    }

    let newMode

    for (const gesture of this.gestures) {
    // Call the event handler for this particular gesture definition
      await callback(gesture)

      if (gesture.requestedMode) {
        if (newMode) {
          this.error("Multiple gestures requested mode switch", gesture)
          throw new Error("Multiple gestures requested mode switch")
        }
        newMode = gesture.requestedMode
        // console.debug("new mode", newMode, gesture)
        gesture.unswitchMode()
      }
      // A gesture can "capture" the gesture stack, meaning they prevent gestures lower
      // in the stack to affect the event. If a gesture captures the stack in this way,
      // we break early (and reset its capture flag)
      if (gesture.isCapturing) {
        gesture.uncapture()
        break
      }
    }
    if (newMode) {
    // console.debug('Switching mode', newMode)
      this.state.value.gesture = newMode
    }

    // Now we must unset any expired gesture variables
    if (event.type === "mouseup")
      this.resetGestureState()
  }

  resetGestureState = () => {
    this.state.value.sidebarItemDraggedOver = undefined
    this.state.value.collectionFileDraggedOver = undefined
    this.state.value.pathFileDraggedOver = undefined
    this.state.value.filesDragged = undefined
    this.state.value.searchDragged = undefined
    this.state.value.mouseDown = undefined
    this.state.value.gesture = "none"
  }

  resetKeyboardState = () => {
    this.state.value.keysPressed = new Set()
  }

  isEditingTextInput = () => {
    const activeElement = document.activeElement
    return activeElement?.tagName === "INPUT"
      || activeElement?.tagName === "TEXTAREA"
      || activeElement?.attributes.getNamedItem("contenteditable")
  }

  /**
   * Determines whether the mouse event was a primary click (e.g. a regular mousedown without
   * a context-menu intent). On mac, users might do ctrl + click to do a context menu click
   * @param event the mouse event firing this
   */
  isPrimaryClick = (event?: MouseEvent) => {
    return event?.button === 0 && !this.isContextMenuClick(event)
  }

  /**
   * Determines whether the mouse event was a context menu click.
   * TODO: On mac, users might do ctrl + click to do a context menu click
   * @param event the mouse event firing this
   */
  isContextMenuClick = (event?: MouseEvent) => {
    if (isMac())
      return event?.button === 2 || (event?.button === 0 && event.ctrlKey)
    return event?.button === 2
  }

  isCommandKeypress = (event: KeyboardEvent) => {
    return isMac() ? event.metaKey : event.ctrlKey
  }

  isCommandKeyChord = () => {
    if (isMac())
      return this.state.value.keysPressed.has("Meta")
    return this.state.value.keysPressed.has("Control")
  }

  isShiftKeyChord = () => {
    return this.state.value.keysPressed.has("Shift")
  }

  isOverCollectionView = (mouse: MouseEvent) => {
    const { collectionScrollContainerId } = useGlobals()
    return getFirstMatchingParent(
      mouse.target,
      e => e.id === collectionScrollContainerId,
    )
  }

  isOverWaterfall = (mouse: MouseEvent) => {
    const { panelWaterfallId } = useGlobals()
    return getFirstMatchingParent(
      mouse.target,
      e => e.id === panelWaterfallId,
    )
  }

  isOverSidebar = (mouse: MouseEvent) => {
    const { sidebarId } = useGlobals()
    return getFirstMatchingParent(mouse.target, e => e.id === sidebarId)
  }

  isOverPath = (mouse: MouseEvent) => {
    const { navOmniBarId: pathId } = useGlobals()
    return getFirstMatchingParent(mouse.target, e => e.id === pathId)
  }

  isOverPanel = (mouse: MouseEvent) => {
    const { panelId } = useGlobals()
    return getFirstMatchingParent(mouse.target, e => e.id === panelId)
  }

  updateMouseButtonsPressedFromEvent = (event: MouseEvent) => {
    const allPressed = event.buttons
    if (!allPressed) {
      this.state.value.mouseButtonsPressed = new Set()
      return
    }
    const pressed = new Set<number>()
    if (allPressed & 1)
      pressed.add(0) // main button
    if (allPressed & 2)
      pressed.add(2) // right button
    if (allPressed & 4)
      pressed.add(1) // middle button
    this.state.value.mouseButtonsPressed = pressed
  }

  updateMousePositionFromEvent = (event: MouseEvent) => {
    this.state.value.mousePosition = {
      clientX: event.clientX,
      clientY: event.clientY,
    }
  }

  getFirstMatchingClassFromEvent = (event: MouseEvent, className: string) => {
    return getFirstMatchingParent(
      event.target,
      t => t.classList?.contains(className),
    )
  }

  getFileIdFromEvent = (event: MouseEvent) => {
    return getFirstMatchingParent(
      event.target,
      t => !!t.dataset?.fileId,
    )?.dataset?.fileId
  }

  getSearchIdFromEvent = (event: MouseEvent) => {
    return getFirstMatchingParent(
      event.target,
      t => !!t.dataset?.searchId,
    )?.dataset?.searchId
  }

  getConversationIdFromEvent = (event: MouseEvent) => {
    return getFirstMatchingParent(
      event.target,
      t => !!t.dataset?.conversationId,
    )?.dataset?.conversationId
  }

  getPathFileElementFromEvent = (event: MouseEvent) => {
    return getFirstMatchingParent(
      event.target,
      t => !!t.dataset?.pathFileId,
    )
  }

  getPathFileIdFromEvent = (event: MouseEvent) => {
    return this.getPathFileElementFromEvent(event)?.dataset?.pathFileId
  }

  getPathSearchElementFromEvent = (event: MouseEvent) => {
    return getFirstMatchingParent(
      event.target,
      t => !!t.dataset?.pathSearchId,
    )
  }

  getPathSearchIdFromEvent = (event: MouseEvent) => {
    return this.getPathSearchElementFromEvent(event)?.dataset?.pathSearchId
  }

  getPropertyIdFromEvent = (event: MouseEvent) => {
    return getFirstMatchingParent(
      event.target,
      t => !!t.dataset?.propertyId,
    )?.dataset?.propertyId as PropertyId | undefined
  }

  getCollectionViewFileFromEvent = (event: MouseEvent) => {
    const { getAllFilesInCollection } = useCollectionView()
    const fileId = this.getFileIdFromEvent(event)
    if (!fileId)
      return undefined
    return getAllFilesInCollection()[fileId]
  }

  getColumnViewFolderFromEvent = (event: MouseEvent) => {
    const { columnViewFolderIdProp } = useGlobals()
    const { contentsByColumn } = useColumnView()
    const folderId = getFirstMatchingParent(
      event.target,
      t => !!t.dataset?.[columnViewFolderIdProp],
    )?.dataset?.[columnViewFolderIdProp]
    if (isDefined(folderId))
      for (const { folder } of contentsByColumn.value)
        if (folder.id === folderId)
          return folder
  }

  getSidebarFavoritesSearchFromEvent = (_event: MouseEvent): StaticSearch | undefined => {
    const { sidebarFavoriteSearches } = useSidebar()
    const searchId = this.getSearchIdFromEvent(_event)
    if (!searchId)
      return
    return sidebarFavoriteSearches.value[searchId]
  }

  getSidebarFavoritesFileFromEvent = (event: MouseEvent) => {
    const { sidebarFavoriteFiles } = useSidebar()
    const fileId = this.getFileIdFromEvent(event)
    if (!fileId)
      return
    return sidebarFavoriteFiles.value[fileId]
  }

  getSidebarFavoritesConversationFromEvent = (event: MouseEvent) => {
    const { sidebarFavoriteConversations } = useSidebar()
    const conversationId = this.getConversationIdFromEvent(event)
    if (!conversationId)
      return
    return sidebarFavoriteConversations.value[conversationId]
  }

  /**
   * Uses the "file id" to retrieve the shared drive root
   */
  getSidebarRootFromEvent = (event: MouseEvent) => {
    const { state: sharedDriveState } = useSharedDrives()
    const { state: myself } = useSelf()
    const fileId = this.getFileIdFromEvent(event)
    if (!fileId)
      return
    if (fileId === myself.value.home?.id)
      return myself.value.home
    if (fileId === myself.value.archive?.id)
      return myself.value.archive
    for (const { home } of Object.values(sharedDriveState.value.all)) {
      if (home.id === fileId)
        return home
    }
  }

  getSidebarRootsFromEvent = (event: MouseEvent) => {
    const { state: myself } = useSelf()
    const fileId = this.getFileIdFromEvent(event)
    if (!fileId)
      return
    if (fileId === myself.value.home?.id)
      return myself.value.home
    if (fileId === myself.value.archive?.id)
      return myself.value.archive
    throw new Error("Getting sidebar roots should only yield home or archive")
  }

  getPanelConversationFromEvent = (event: MouseEvent): StaticConversation | undefined => {
    const { state: conversationState } = useConversations()
    const conversationId = this.getConversationIdFromEvent(event)

    if (!conversationId)
      return undefined

    return conversationState.value.all[conversationId]
  }

  getPathFileFromEvent = (event: MouseEvent): StaticFile | undefined => {
    const { currentRootFolder, currentFilePreview } = useNavigation()
    const fileId = this.getPathFileIdFromEvent(event)
    const pathFile = currentRootFolder.value ?? currentFilePreview.value
    if (!fileId || !pathFile)
      return
    if (pathFile.id === fileId)
      return pathFile.asStatic()
    for (const file of pathFile.path() || [])
      if (file.id === fileId)
        return file
    this.error("Path file id didn't match anything")
    return
  }

  getPathSearchFromEvent = (event: MouseEvent): StaticSearch | undefined => {
    const { currentSearch } = useNavigation()
    const searchId = this.getPathSearchIdFromEvent(event)
    if (!searchId || !currentSearch.value)
      return
    if (currentSearch.value.id === searchId)
      return currentSearch.value.asStatic()
    this.error("Path search id didn't match anything")
    return
  }

  clickedCloseEnough = (
    a: { clientX: number, clientY: number },
    b: { clientX: number, clientY: number },
    tolerancePx = 3,
  ) => {
    const dist = Math.sqrt(
      (a.clientX - b.clientX) ** 2 + (a.clientY - b.clientY) ** 2,
    )
    return dist < tolerancePx
  }
}

const gestures = new Gestures("gestures", theme.colors.blue.hex)
export default function () {
  return gestures.use()
}
